import { apiClient } from './api-client';

export interface DocumentPreview {
  documentId: string;
  fileName: string;
  thumbnailUrl: string;
  uploadedAt: string;
  verified: boolean;
}

export interface SecureDocumentUrl {
  documentId: string;
  viewUrl: string;
  expiresAt: string;
  watermarked: boolean;
}

export interface DocumentMetadata {
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  associatedBooking: string;
}

export class DocumentService {
  private static previewCache = new Map<string, { data: DocumentPreview; timestamp: number }>();
  private static CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  // Get document preview (thumbnail) with caching
  static async getDocumentPreview(documentId: string, forceRefresh = false): Promise<DocumentPreview> {
    console.log(`[DocumentService] getDocumentPreview called for ${documentId}, forceRefresh: ${forceRefresh}`);
    
    const cacheKey = documentId;
    const cached = this.previewCache.get(cacheKey);
    
    // Check if we have valid cached data
    if (!forceRefresh && cached) {
      const isUrlExpired = this.isUrlExpired(cached.data.thumbnailUrl);
      const isCacheStale = Date.now() - cached.timestamp > this.CACHE_DURATION;
      
      console.log(`[DocumentService] Cache check for ${documentId}:`, {
        hasCache: true,
        isUrlExpired,
        isCacheStale,
        cacheAge: Date.now() - cached.timestamp
      });
      
      if (!isUrlExpired && !isCacheStale) {
        console.log(`[DocumentService] Returning cached data for ${documentId}`);
        return cached.data;
      }
    }
    
    // Fetch fresh data
    console.log(`[DocumentService] Fetching fresh data for ${documentId}`);
    try {
      const response = await apiClient.get<DocumentPreview>(`/documents/${documentId}/preview`);
      console.log(`[DocumentService] API response for ${documentId}:`, response);
      console.log(`[DocumentService] Preview data for ${documentId}:`, response.data);
      
      // Cache the response
      this.previewCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
      return response.data;
    } catch (error) {
      console.error(`[DocumentService] API error for ${documentId}:`, error);
      throw error;
    }
  }

  // Get secure document URL for full viewing
  static async getSecureDocumentUrl(documentId: string): Promise<SecureDocumentUrl> {
    const response = await apiClient.get<SecureDocumentUrl>(`/documents/${documentId}/view`);
    return response.data;
  }

  // Get multiple document previews
  static async getDocumentPreviews(documentIds: string[]): Promise<DocumentPreview[]> {
    const response = await apiClient.post<DocumentPreview[]>('/documents/previews', {
      documentIds
    });
    return response.data;
  }

  // Generate presigned upload URL for check-in form
  static async generateUploadUrl(
    token: string, 
    documentType: 'id_front' | 'id_back',
    fileName: string,
    fileSize: number,
    mimeType: string
  ): Promise<{
    uploadUrl: string;
    uploadId: string;
    fields: Record<string, string>;
  }> {
    const response = await apiClient.post<{
      uploadUrl: string;
      uploadId: string;
      fields: Record<string, string>;
    }>(`/check-in-form/upload-url/${token}`, {
      documentType,
      fileName,
      fileSize,
      mimeType
    });
    return response.data;
  }

  // Confirm document upload for check-in form
  static async confirmDocumentUpload(token: string, uploadId: string, metadata?: any): Promise<void> {
    await apiClient.post(`/check-in-form/confirm-upload/${token}`, {
      uploadId,
      metadata
    });
  }

  // Delete document
  static async deleteDocument(documentId: string, reason: string): Promise<void> {
    await apiClient.delete(`/documents/${documentId}?reason=${encodeURIComponent(reason)}`);
  }

  // Get document access log
  static async getDocumentAccessLog(documentId: string): Promise<any[]> {
    const response = await apiClient.get<any[]>(`/documents/${documentId}/access-log`);
    return response.data;
  }

  // Helper function to determine if file is an image
  static isImageFile(fileName: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  // Helper function to get file size display
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Helper function to check if S3 URL is expired
  static isUrlExpired(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
      const dateParam = urlObj.searchParams.get('X-Amz-Date');
      
      if (!dateParam || !expiresParam) {
        console.log(`[DocumentService] URL is not a signed URL or missing expiration params:`, { url, dateParam, expiresParam });
        return false; // Not a signed URL or can't determine expiration
      }
      
      // Parse the date parameter (format: 20250928T084127Z)
      const signedDate = new Date(
        dateParam.substring(0, 4) + '-' +
        dateParam.substring(4, 6) + '-' +
        dateParam.substring(6, 8) + 'T' +
        dateParam.substring(9, 11) + ':' +
        dateParam.substring(11, 13) + ':' +
        dateParam.substring(13, 15) + 'Z'
      );
      
      const expirationDate = new Date(signedDate.getTime() + parseInt(expiresParam) * 1000);
      const now = new Date();
      const isExpired = now >= expirationDate;
      
      console.log(`[DocumentService] URL expiration check:`, {
        url: url.substring(0, 100) + '...',
        signedDate: signedDate.toISOString(),
        expirationDate: expirationDate.toISOString(),
        now: now.toISOString(),
        isExpired
      });
      
      return isExpired;
    } catch (error) {
      console.error('[DocumentService] Error checking URL expiration:', error);
      return false;
    }
  }

  // Helper function to validate file type
  static validateFileType(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'File type not allowed. Please upload JPEG, PNG, GIF, or PDF files only.'
      };
    }
    
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size too large. Maximum size is 10MB.'
      };
    }
    
    return { valid: true };
  }
}

export default DocumentService;